import { useState } from 'react';
import './NewPatentApplicationForm.css'
import { createPatentApplication } from '../../utilities/api/patentApplications';


export default function NewPatentApplicationForm({user, checkForClient, updatePatentApplications, toggleNotifications}) {
    const defaultState = {
        title: '',
        clientName: '',
        clientID: null,
        RPNumber: '',
        applicants: '',
        inventors: '',
        filingFirm: '',
        firmRefNumber: '',
        attorney: '',
        attorneyRefNumber: '',
        contact: '',
        country: '',
        patentType: '',
        applicationType: '',
        applicationStatus: '',
        filingStatus: '',
        earliestPriorityDate: '',
        earliestPriorityApplicationNumber: '',
        applicationNumber: '',
        filingDate: '',
        plannedFilingDate: '',
        officialFilingDate: '',
        lastAction: '',
        nextAction: '',
        nextActionDueDate: '',
        entityStatus: '',
        nationalEntryDate: '',
        publicationDate: '',
        estimatedFirstOfficeActionDate: '',
        officialOfficeActionDate: '',
        allowanceDate: '',
        requestExaminerInterview: '',
        examinerInterviewDate: '',
        filedRCE: '',
        RCEFiledDate: '',
        issueFeePaymentDate: '',
        issueDate: '',
        patentNumber: '',
        maintenanceFeeDueDate: '',
        maintenanceFeeStatus: '',
        expirationDate: '',
        abandonedDate: '',
        PAIRReg: '',
        notes: '',
        driveFolderLocation: '',
    }
    const [formData, setFormData] = useState(defaultState)
    const rpNumberReg = /^([A-Z]{2}-[0-9]{4}\.[0-9]{2,}\.[0-9]{3}.*)$/
    const [isValidRPNumber, setIsValidRPNumber] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)

    const populateDates = async (formData) => {
        try {
            if (formData.applicationType === 'provisional' && formData.filingDate) {
                const filingDate = new Date(formData.filingDate)
                const expirationDate = new Date(filingDate)
                expirationDate.setFullYear(expirationDate.getFullYear() + 1)
                setFormData(prevFormData => ({
                    ...prevFormData,
                    expirationDate: expirationDate.toISOString().split('T')[0]
                }))
            } else if (formData.applicationType === 'canadian' && formData.filingDate) {
                const filingDate = new Date(formData.filingDate)
                const maintenanceFeeDueDate = new Date(filingDate)
                maintenanceFeeDueDate.setFullYear(maintenanceFeeDueDate.getFullYear() + 2)
                setFormData(prevFormData => ({
                    ...prevFormData,
                    maintenanceFeeDueDate: maintenanceFeeDueDate.toISOString().split('T')[0]
                }))
            } else if (formData.applicationType === 'PCT' && formData.filingDate) {
                const filingDate = new Date(formData.filingDate)
                const expirationDate = new Date(filingDate)
                // Add 30 months properly by calculating years and remaining months
                const years = Math.floor(30 / 12)
                const months = 30 % 12
                expirationDate.setFullYear(expirationDate.getFullYear() + years)
                expirationDate.setMonth(expirationDate.getMonth() + months)
                setFormData(prevFormData => ({
                    ...prevFormData,
                    expirationDate: expirationDate.toISOString().split('T')[0]
                }))
            }
        } catch (error) {
            console.log(error)
        }
    }

    const handleSubmit = async (evt) =>{
        evt.preventDefault();
        setIsSubmitting(true)
        try {
            const updatedPatentApplicationList = await createPatentApplication(formData)
            updatePatentApplications(updatedPatentApplicationList)
            await toggleNotifications('success', `New Patent Application "${formData.applicationNumber}" Created!`, '#FFFFFF')
            setFormData(defaultState)
            document.getElementById('newPatentApplicationForm').reset()
        } catch (error) {
            console.log(error)
            await toggleNotifications('error', `Failed to Create Patent Application "${formData.applicationNumber}"!`, '#FFFFFF')
        }
        setIsSubmitting(false)
    }

    async function handleChange(evt) {
        const newFormData = {
          ...formData,
          [evt.target.name]: evt.target.value,
        }

        if (evt.target.name === 'RPNumber') {
            await checkRPNumber(evt)
        }

        setFormData(newFormData);
        
        if (evt.target.name === 'clientID') {
            const clientID = evt.target.value
            if (clientID) {
                // Use async/await in an IIFE (Immediately Invoked Function Expression) to avoid blocking UI
                (async () => {
                    try {
                        const client = await checkForClient(clientID);
                        setFormData(prevFormData => ({
                            ...prevFormData,
                            clientName: client ? client.companyName : ''
                        }));
                    } catch (error) {
                        console.error('Error checking for client:', error);
                        setFormData(prevFormData => ({
                            ...prevFormData,
                            clientName: ''
                        }));
                    }
                })();
            } else {
                // Clear client name if clientID is empty
                setFormData(prevFormData => ({
                    ...prevFormData,
                    clientName: ''
                }));
            }
        }

        if (evt.target.name === 'applicationType') {
            // Clear auto-populated fields when application type changes
            newFormData.expirationDate = ''
            newFormData.maintenanceFeeDueDate = ''
        }

        if (evt.target.name === 'applicationType' || evt.target.name === 'filingDate') {
            await populateDates(newFormData)
        }
    }

    const checkRPNumber = async (evt) => {
        const rpNumber = evt.target.value
        rpNumberReg.test(rpNumber) ? setIsValidRPNumber(true) : setIsValidRPNumber(false)
    }


    const disabled = !formData.title || !formData.clientID || !formData.clientName || !formData.RPNumber || !isValidRPNumber || !formData.applicationNumber || !formData.filingDate
  return (
    <div className='NewPatentApplicationForm'>
        <form onSubmit={handleSubmit} id={`newPatentApplicationForm`}>
            <div className='formContent'>
                <div className='form-group'>
                    <label htmlFor={`patent-title`} className='form-label'>Patent Title <span className='requiredField'><strong>*</strong></span></label>
                    <input type="text" id={`patent-title`} name='title' className='form-control' defaultValue={formData.title} onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-clientID`} className='form-label'>Client ID <span className='requiredField'><strong>*</strong></span></label>
                    <input type="number" min={0} id={`patent-clientID`} name='clientID' className='form-control' defaultValue={formData.clientID} onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-clientName`} className='form-label'>Client Name <span className='requiredField'><strong>*</strong></span></label>
                    <input type="text" id={`patent-clientName`} name='clientName' className='form-control' defaultValue={formData.clientName} onChange={handleChange}/>
                </div>
                <div className='form-group rpNumber'>
                    <div className='input'>
                        <label htmlFor={`patent-RPNumber`} className='form-label'>RP Number <span className='requiredField'><strong>*</strong></span></label>
                        <input type="text" id={`patent-RPNumber`} name='RPNumber' className='form-control' defaultValue={formData.RPNumber} onChange={handleChange}/>
                    </div>
                    <div className='rpNumberStatus'>
                        <small className="form-text checkIsValidRPNumber" style={{backgroundColor:`${isValidRPNumber? 'green' : 'red'}`, color:'white'}}>Format: XX-0000.00.000</small>
                    </div>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-applicants`} className='form-label'>Applicants</label>
                    <input type="text" id={`patent-applicants`} name='applicants' className='form-control' defaultValue={formData.applicants} onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-inventors`} className='form-label'>Inventors</label>
                    <input type="text" id={`patent-inventors`} name='inventors' className='form-control' defaultValue={formData.inventors} onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-filingFirm`} className='form-label'>Filing Firm</label>
                    <input type="text" id={`patent-filingFirm`} name='filingFirm' className='form-control' defaultValue={formData.filingFirm} onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-firmRefNumber`} className='form-label'>Filing Firm Ref Number</label>
                    <input type="text" id={`patent-firmRefNumber`} name='firmRefNumber' className='form-control' defaultValue={formData.firmRefNumber} onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-attorney`} className='form-label'>Attorney</label>
                    <input type="text" id={`patent-attorney`} name='attorney' className='form-control' defaultValue={formData.attorney} onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-attorneyRefNumber`} className='form-label'>Attorney Ref Number</label>
                    <input type="text" id={`patent-attorneyRefNumber`} name='attorneyRefNumber' className='form-control' defaultValue={formData.attorneyRefNumber} onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-contact`} className='form-label'>Client Contact</label>
                    <input type="text" id={`patent-contact`} name='contact' className='form-control' defaultValue={formData.contact} onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-country`} className='form-label'>Country</label>
                    <input type="text" id={`patent-country`} name='country' className='form-control' defaultValue={formData.country} onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-patentType`} className='form-label'>Patent Type</label>
                    <select id={`patent-patentType`} name='patentType' className='form-select' defaultValue={formData.patentType} onChange={handleChange}>
                        <option value="">---Select Patent Type---</option>
                        <option value="utility">Utility</option>
                        <option value="design">Design</option>
                    </select>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-applicationType`} className='form-label'>Application Type</label>
                    <select id={`patent-applicationType`} name='applicationType' className='form-select' defaultValue={formData.applicationType} onChange={handleChange}>
                        <option value="">---Select Application Type---</option>
                        <option value="provisional">Provisional</option>
                        <option value="nonProvisional">Non-Provisional</option>
                        <option value="PCT">PCT</option>
                        <option value="canadian">Canadian</option>
                        <option value="foreign">Foreign</option>
                    </select>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-applicationStatus`} className='form-label'>Application Status</label>
                    <select id={`patent-applicationStatus`} name='applicationStatus' className='form-select' defaultValue={formData.applicationStatus} onChange={handleChange}>
                        <option className='empty' value="">---Select Application Status---</option>
                        <option value="abandoned">Abandoned</option>
                        <option value="allowed">Allowed</option>
                        <option value="approved">Approved</option>
                        <option value="awaitingExam">Awaiting Exam</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="expired">Expired</option>
                        <option value="filed">Filed</option>
                        <option value="granted">Granted</option>
                        <option value="NOAIssued">NOA Issued</option>
                        <option value="officeActionIssued">Office Action Issued</option>
                        <option value="published">Published</option>
                    </select>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-earliestPriorityDate`} className='form-label'>Earliest Priority Date</label>
                    <input type="date" id={`patent-earliestPriorityDate`} name='earliestPriorityDate' className='form-control' defaultValue={formData.earliestPriorityDate} onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-earliestPriorityApplicationNumber`} className='form-label'>Earliest Priority Application Number</label>
                    <input type="text" id={`patent-earliestPriorityApplicationNumber`} name='earliestPriorityApplicationNumber' className='form-control' defaultValue={formData.earliestPriorityApplicationNumber}  onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-lastAction`} className='form-label'>Last Action</label>
                    <input type="text" id={`patent-lastAction`} name='lastAction' className='form-control' defaultValue={formData.lastAction} onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-nextAction`} className='form-label'>Next Action</label>
                    <input type="text" id={`patent-nextAction`} name='nextAction' className='form-control' defaultValue={formData.nextAction} onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-nextActionDueDate`} className='form-label'>Next Action Due</label>
                    <input type="date" id={`patent-nextActionDueDate`} name='nextActionDueDate' className='form-control' defaultValue={formData.nextActionDueDate} onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-entityStatus`} className='form-label'>Entity Status</label>
                    <select id={`patent-entityStatus`} name='entityStatus' className='form-select' defaultValue={formData.entityStatus} onChange={handleChange}>
                        <option value="">---Select Entity Status---</option>
                        <option value="micro">Micro</option>
                        <option value="small">Small</option>
                        <option value="large">Large</option>
                    </select>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-applicationNumber`} className='form-label'>Application Number <span className='requiredField'><strong>*</strong></span></label>
                    <input type="text" id={`patent-applicationNumber`} name='applicationNumber' className='form-control' defaultValue={formData.applicationNumber}  onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-filingDate`} className='form-label'>Filing Date <span className='requiredField'><strong>*</strong></span></label>
                    <input type="date" id={`patent-filingDate`} name='filingDate' className='form-control' defaultValue={formData.filingDate} onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-plannedFilingDate`} className='form-label'>Planned Filing Date</label>
                    <input type="date" id={`patent-plannedFilingDate`} name='plannedFilingDate' className='form-control' defaultValue={formData.plannedFilingDate} onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-officialFilingDate`} className='form-label'>Official Filing Date</label>
                    <input type="date" id={`patent-officialFilingDate`} name='officialFilingDate' className='form-control' defaultValue={formData.officialFilingDate} onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-nationalEntryDate`} className='form-label'>National Entry Date</label>
                    <input type="date" id={`patent-nationalEntryDate`} name='nationalEntryDate' className='form-control' defaultValue={formData.nationalEntryDate} onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-publicationDate`} className='form-label'>Publication Date</label>
                    <input type="date" id={`patent-publicationDate`} name='publicationDate' className='form-control' defaultValue={formData.publicationDate} onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-estimatedFirstOfficeActionDate`} className='form-label'>Estimated First Office Action Date</label>
                    <input type="date" id={`patent-estimatedFirstOfficeActionDate`} name='estimatedFirstOfficeActionDate' className='form-control' defaultValue={formData.estimatedFirstOfficeActionDate} onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-officialOfficeActionDate`} className='form-label'>Official Office Action Date</label>
                    <input type="date" id={`patent-officialOfficeActionDate`} name='officialOfficeActionDate' className='form-control' defaultValue={formData.officialOfficeActionDate} onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-allowanceDate`} className='form-label'>Allowance Date</label>
                    <input type="date" id={`patent-allowanceDate`} name='allowanceDate' className='form-control' defaultValue={formData.allowanceDate} onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-requestExaminerInterview`} className='form-label'>Request Examiner Interview</label>
                    <select id={`patent-requestExaminerInterview`} name='requestExaminerInterview' className='form-select' defaultValue={formData.requestExaminerInterview} onChange={handleChange}>
                        <option value="">---Select Option---</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                    </select>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-examinerInterviewDate`} className='form-label'>Examiner Interview Date</label>
                    <input type="date" id={`patent-examinerInterviewDate`} name='examinerInterviewDate' className='form-control' defaultValue={formData.examinerInterviewDate} onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-filedRCE`} className='form-label'>Filed RCE</label>
                    <select id={`patent-filedRCE`} name='filedRCE' className='form-select' defaultValue={formData.filedRCE} onChange={handleChange}>
                        <option value="">---Select Option---</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                    </select>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-RCEFiledDate`} className='form-label'>RCE Filing Date</label>
                    <input type="date" id={`patent-RCEFiledDate`} name='RCEFiledDate' className='form-control' defaultValue={formData.RCEFiledDate} onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-issueFeePaymentDate`} className='form-label'>Issue Fee Payment Date</label>
                    <input type="date" id={`patent-issueFeePaymentDate`} name='issueFeePaymentDate' className='form-control' defaultValue={formData.issueFeePaymentDate} onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-issueDate`} className='form-label'>Issue Date</label>
                    <input type="date" id={`patent-issueDate`} name='issueDate' className='form-control' defaultValue={formData.issueDate} onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-patentNumber`} className='form-label'>Patent Number</label>
                    <input type="text" id={`patent-patentNumber`} name='patentNumber' className='form-control' defaultValue={formData.patentNumber}  onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-maintenanceFeeDueDate`} className='form-label'>Maintenance Fee Date</label>
                    <input type="date" id={`patent-maintenanceFeeDueDate`} name='maintenanceFeeDueDate' className='form-control' defaultValue={formData.maintenanceFeeDueDate} onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-maintenanceFeeStatus`} className='form-label'>Maintenance Fee Status</label>
                    <select id={`patent-maintenanceFeeStatus`} name='maintenanceFeeStatus' className='form-select' defaultValue={formData.maintenanceFeeStatus} onChange={handleChange}>
                        <option value="">---Select Option---</option>
                        <option value="paid">Paid</option>
                        <option value="unpaid">Unpaid</option>
                    </select>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-expirationDate`} className='form-label'>Expiration Date</label>
                    <input type="date" id={`patent-expirationDate`} name='expirationDate' className='form-control' defaultValue={formData.expirationDate} onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-abandonedDate`} className='form-label'>Abandoned Date</label>
                    <input type="date" id={`patent-abandonedDate`} name='abandonedDate' className='form-control' defaultValue={formData.abandonedDate} onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-PAIRReg`} className='form-label'>PAIR Reg</label>
                    <input type="text" id={`patent-PAIRReg`} name='PAIRReg' className='form-control' defaultValue={formData.PAIRReg} onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-notes`} className='form-label'>Notes</label>
                    <textarea id={`patent-notes`} name='notes' className='form-control' defaultValue={formData.notes} onChange={handleChange}/>
                </div>
                <div className='form-group'>
                    <label htmlFor={`patent-driveFolderLocation`} className='form-label'>Folder Location</label>
                    <input type="text" id={`patent-driveFolderLocation`} name='driveFolderLocation' className='form-control' defaultValue={formData.driveFolderLocation} onChange={handleChange}/>
                </div>
            </div>
        </form>
        <div className='formButtonContainer'>
            <button data-bs-dismiss="modal" className='cancel'>
                Cancel
            </button>
            <button data-bs-dismiss="modal" className="submit" type="submit" form={`newPatentApplicationForm`} disabled={disabled||isSubmitting}>
                Add Application
            </button>
        </div>
    </div>
  )
}
